import { Authenticated, AuthLoading, Unauthenticated, useQuery } from "convex/react";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { api } from "../convex/_generated/api";
import { AppShell } from "./components/AppShell";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { FullScreenLoader } from "./components/ui";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { BlacklistPage } from "./pages/BlacklistPage";
import { ChecksPage } from "./pages/ChecksPage";
import { CheckDetailPage } from "./pages/CheckDetailPage";
import { SuspectsPage } from "./pages/SuspectsPage";
import { WatchPage } from "./pages/WatchPage";
import { UsersPage } from "./pages/UsersPage";
import { ApiKeysPage } from "./pages/ApiKeysPage";
import { AuditPage } from "./pages/AuditPage";
import { SettingsPage } from "./pages/SettingsPage";

/** Remonte l'ErrorBoundary quand l'utilisateur navigue : une erreur ne doit pas figer les autres pages. */
function RoutedContent() {
  const location = useLocation();
  return (
    <ErrorBoundary key={location.pathname}>
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/blacklist" element={<BlacklistPage />} />
        <Route element={<StaffOnly />}>
          <Route path="/controles" element={<ChecksPage />} />
          <Route path="/controles/:checkId" element={<CheckDetailPage />} />
          <Route path="/surveillance" element={<WatchPage />} />
          <Route path="/suspects" element={<SuspectsPage />} />
        </Route>
        <Route element={<OwnerOnly />}>
          <Route path="/admin/utilisateurs" element={<UsersPage />} />
          <Route path="/admin/api" element={<ApiKeysPage />} />
          <Route path="/admin/audit" element={<AuditPage />} />
          <Route path="/admin/parametres" element={<SettingsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}

function ProtectedApplication() {
  return (
    <BrowserRouter>
      {/* Enveloppe aussi l'en-tête : une de ses requêtes qui échoue ne doit pas vider la page. */}
      <ErrorBoundary>
        <AppShell><RoutedContent /></AppShell>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

/**
 * Un visiteur ne consulte que la blacklist et la vue d'ensemble. Le serveur refuse déjà ses
 * requêtes sur les autres sections ; cette redirection lui évite simplement d'atterrir sur un
 * écran d'erreur en tapant l'adresse à la main.
 */
function StaffOnly() {
  const me = useQuery(api.users.me);
  if (me === undefined) return <FullScreenLoader label="Vérification des droits…" />;
  return me && me.role !== "visitor" ? <Outlet /> : <Navigate to="/" replace />;
}

function OwnerOnly() {
  const me = useQuery(api.users.me);
  // `undefined` = en cours de chargement ; `null` = compte sans accès, à rediriger sans attendre.
  if (me === undefined) return <FullScreenLoader label="Vérification des droits…" />;
  return me?.isOwner ? <Outlet /> : <Navigate to="/" replace />;
}

export default function App() {
  return (
    <>
      <AuthLoading><FullScreenLoader label="Ouverture de la salle de contrôle…" /></AuthLoading>
      <Unauthenticated><LoginPage /></Unauthenticated>
      <Authenticated><ProtectedApplication /></Authenticated>
    </>
  );
}
